import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import DualDate from '../../components/common/DualDate';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import toast from 'react-hot-toast';
import useImpersonation from '../../hooks/useImpersonation';
import Card from '../../components/common/Card';
import OpeningBalancesCard from '../../components/admin/OpeningBalancesCard';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import PaymentMethodsSection from '../../components/admin/PaymentMethodsSection';
import { ACCEPTED_METHOD_OPTIONS as PAYMENT_METHODS } from '../../utils/paymentMethods';
import Modal from '../../components/common/Modal';
import { getSettings, updateSettings } from '../../services/settingsService';
import { updateCalendarPreference } from '../../services/userService';
import { updateClubSettings } from '../../services/clubService';
import { sanitizePhone, isValidPhone, sanitizeEmail, isValidEmail, PHONE_HINT, EMAIL_HINT } from '../../utils/inputRules';
import AvatarUploader from '../../components/AvatarUploader';
import useTheme from '../../context/ThemeContext';
import { useCalendar } from '../../context/CalendarContext';
import {
  MdSettings, MdLocationOn, MdPayments, MdSave, MdPalette,
  MdShield, MdCalendarMonth, MdCardMembership,
  MdNotifications, MdAccessTime,
} from 'react-icons/md';
import useAuth from '../../hooks/useAuth';
import useClubVocab from '../../hooks/useClubVocab';
import api from '../../services/api';
import { useTenant } from '../../context/TenantContext';
import MembershipPlansSection from '../../components/admin/MembershipPlansSection';
import { hasBelts } from '../../utils/publicProfile';
import { BELT_ORDER } from '../../constants/belts';

// ── Constants ────────────────────────────────────────────────────────────────
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const TIMEZONES = [
  'Asia/Kathmandu', 'Asia/Kolkata', 'Asia/Bangkok', 'Asia/Singapore',
  'Asia/Tokyo', 'Asia/Dubai', 'Europe/London', 'Europe/Paris',
  'Australia/Sydney', 'America/New_York', 'America/Los_Angeles', 'UTC',
];

// Canonical Stage-1 verticals — this dropdown reads/writes the canonical Club.clubType (the
// field that drives all wording), NOT the old orphan Settings.clubType. Keep in sync with the
// Club enum and utils/publicProfile.js vocab().
const CLUB_TYPES = [
  { value: 'taekwondo', label: 'Taekwondo (Students)' },
  { value: 'gym',       label: 'Gym (Members)' },
  { value: 'yoga',      label: 'Yoga (Members · Instructors)' },
  { value: 'general',   label: 'General / Other (Members)' },
];

const NAV_ITEMS = [
  { id: 'club-info',        label: 'Club Info',             Icon: MdSettings },
  { id: 'location',         label: 'Location & Check-in',   Icon: MdLocationOn },
  { id: 'fees',             label: 'Fees & Billing',        Icon: MdPayments },
  { id: 'attendance-rules', label: 'Attendance Rules',      Icon: MdAccessTime },
  { id: 'notifications',    label: 'Notifications',         Icon: MdNotifications },
  { id: 'appearance',       label: 'Appearance',            Icon: MdPalette },
  { id: 'security',         label: 'Security & Access Log', Icon: MdShield },
];

const DEFAULT_HOURS = DAYS.map(day => ({
  day,
  isOpen:    day !== 'Sunday',
  openTime:  ['Saturday'].includes(day) ? '08:00' : '06:00',
  closeTime: ['Saturday'].includes(day) ? '18:00' : '21:00',
}));

const DEFAULT_NOTIFS = {
  newMemberJoins: true, paymentOverdue: true, beltExamScheduled: true,
  trialClassRegistration: true, sessionCreated: true,
  attendanceMarked: true, announcementPosted: true,
};

const SELECT_CLS = 'w-full border border-divider rounded-xl px-3 py-2.5 text-sm bg-card text-secondary placeholder:text-subtle focus:outline-none focus:ring-2 focus:ring-primary/30';

// ── Sub-components ────────────────────────────────────────────────────────────

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 ${
        checked ? 'bg-primary' : 'bg-nav-hover'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-secondary shadow transition ease-in-out ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

function SectionHead({ id, Icon, title }) {
  return (
    <div id={id} className="scroll-mt-6">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="text-primary text-xl flex-shrink-0" />
        <h2 className="font-semibold text-secondary">{title}</h2>
      </div>
      <hr className="border-divider mt-3 mb-5" />
    </div>
  );
}

function FieldLabel({ children, sub }) {
  return (
    <div className="mb-1">
      <label className="block text-sm font-medium text-secondary">{children}</label>
      {sub && <p className="text-xs text-subtle mt-0.5">{sub}</p>}
    </div>
  );
}

function PlatformAccessCard() {
  const { formatDate } = useCalendar();
  const { data, isPending } = useQuery({
    queryKey: ['platform-access'],
    queryFn:  () => api.get('/api/clubs/me/platform-access').then(r => r.data),
  });

  return (
    <div>
      <p className="text-xs text-subtle mb-4">
        This log shows when TrainAxis support has accessed your club for troubleshooting.
        All access requires a reason and is logged permanently.
      </p>
      {isPending ? (
        <p className="text-xs text-subtle">Loading…</p>
      ) : !data?.sessions?.length ? (
        <p className="text-xs text-subtle">No platform access recorded in the last 90 days.</p>
      ) : (
        <div className="space-y-2">
          {data.sessions.map((s, i) => (
            <div key={i} className="flex justify-between items-start text-xs py-2 border-b border-divider last:border-0">
              <div>
                <span className="font-medium text-secondary">{s.superAdminName}</span>
                <span className={`ml-2 px-1.5 py-0.5 rounded text-xs font-semibold ${
                  s.mode === 'full_access' ? 'bg-red-500/10 text-red-400' : 'bg-nav-hover text-muted'
                }`}>
                  {s.mode === 'full_access' ? 'Full Access' : 'Read-Only'}
                </span>
              </div>
              <div className="text-right text-subtle">
                <div><DualDate date={s.startedAt} /></div>
                <div className="max-w-48 truncate">{s.reason}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
// ── Per-account Two-Factor Authentication card (admin only; primary + staff, no adminType
// gate). Uses its OWN per-user mutation (mirrors themeMutation) — never the club settings
// form / sticky Save. Confirming requires the admin's current password.
function TwoFactorCard() {
  const { user, updateUser } = useAuth();
  const currentTheme = useTheme()?.currentTheme;
  const [open, setOpen]         = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');

  const mutation = useMutation({
    mutationFn: ({ enabled, currentPassword }) => api.post('/api/admin/users/me/2fa', { enabled, currentPassword }),
    onSuccess: (res) => {
      const on = res.data.twoFactorEnabled;
      updateUser({ twoFactorEnabled: on });   // flip the status line without a reload
      toast.success(on ? 'Two-factor authentication turned on' : 'Two-factor authentication turned off');
      setOpen(false);
    },
    // Wrong password (or any error) → inline message; keep the modal open.
    onError: (err) => setError(err.response?.data?.message || 'Something went wrong. Please try again.'),
  });

  // Per-account, admin-only — both primary and staff admins (deliberately no adminType gate).
  if (user?.role !== 'admin') return null;

  const enabled       = !!user?.twoFactorEnabled;
  const targetEnabled = !enabled;

  const openModal  = () => { setPassword(''); setError(''); setOpen(true); };
  const closeModal = () => { if (!mutation.isPending) setOpen(false); };
  const submit = (e) => {
    e.preventDefault();
    if (!password || mutation.isPending) return;
    setError('');
    mutation.mutate({ enabled: targetEnabled, currentPassword: password });
  };

  return (
    <Card className="mb-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <MdShield className="text-secondary text-lg" />
            <h3 className="font-semibold text-secondary text-sm">Two-Factor Authentication</h3>
          </div>
          <p className="text-sm text-muted mt-1 max-w-md">
            Adds a 6-digit email code to your sign-in. This applies to your account only.
          </p>
          <div className="flex items-center gap-1.5 mt-3">
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
            <span className="text-sm text-muted">
              2FA is <span className="font-medium text-secondary">{enabled ? 'on' : 'off'}</span> for {user?.email}
            </span>
          </div>
        </div>
        <button
          onClick={openModal}
          className={enabled
            ? 'flex-shrink-0 bg-card text-secondary border border-divider rounded-full font-medium text-sm px-4 py-2 hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200'
            : `flex-shrink-0 ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} rounded-full font-medium text-sm px-4 py-2 hover:opacity-90 transition-all duration-200`}
        >
          {enabled ? 'Turn off 2FA' : 'Turn on 2FA'}
        </button>
      </div>

      <Modal
        isOpen={open}
        onClose={closeModal}
        title={targetEnabled ? 'Turn on two-factor authentication' : 'Turn off two-factor authentication'}
        maxWidth="max-w-sm"
      >
        <form onSubmit={submit}>
          <p className="text-sm text-muted mb-4">
            {targetEnabled
              ? 'Signing in will require a 6-digit code emailed to you. Enter your current password to confirm.'
              : 'Your sign-in will no longer require an email code. Enter your current password to confirm.'}
          </p>
          <input
            type="password"
            autoFocus
            value={password}
            onChange={(e) => { setPassword(e.target.value); if (error) setError(''); }}
            placeholder="Current password"
            autoComplete="current-password"
            className="w-full bg-card border border-divider rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-divider"
          />
          {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
          <div className="flex gap-2 justify-end mt-6">
            <button
              type="button"
              onClick={closeModal}
              disabled={mutation.isPending}
              className="bg-card text-secondary border border-divider rounded-full font-medium text-sm px-4 py-2 hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || !password}
              className={`${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} rounded-full font-medium text-sm px-4 py-2 hover:opacity-90 transition-all duration-200 disabled:opacity-50`}
            >
              {mutation.isPending ? 'Saving…' : (targetEnabled ? 'Turn on 2FA' : 'Turn off 2FA')}
            </button>
          </div>
        </form>
      </Modal>
    </Card>
  );
}

function Settings() {
  document.title = 'Settings — TrainAxis';
  const queryClient  = useQueryClient();
  const { user }     = useAuth();
  const v            = useClubVocab();
  const { slug, club, setClubDirectly } = useTenant();
  const { updateUser } = useAuth();
  const { isReadOnly } = useImpersonation();

  const {
    register, handleSubmit, reset, control, getValues, setValue,
    formState: { errors, isDirty, dirtyFields },
  } = useForm();

  const watchGpsLat = useWatch({ control, name: 'gps.lat' });
  const watchGpsLng = useWatch({ control, name: 'gps.lng' });

  // Path B, Stage 3 — watched so the two buttons below reflect the live (possibly unsaved) choice.
  const billingCalendarW = useWatch({ control, name: 'billingCalendar' });

  // Late-fee policy — watched for the live preview line.
  const lateFeeEnabled = useWatch({ control, name: 'lateFeeEnabled' });
  const feeDueDayW     = useWatch({ control, name: 'feeDueDay' });
  const graceValue     = useWatch({ control, name: 'gracePeriodDays' });
  const graceUnit      = useWatch({ control, name: 'lateFeeGraceUnit' });
  const lateFeeAmountW = useWatch({ control, name: 'lateFeeAmount' });
  // Live, plain-language example built from the club's configured due day + the current grace /
  // amount inputs — e.g. "Example: fee due on day 1, grace 7 days → NPR 100 late fee added
  // automatically from day 8." Falls back to the amount hint when no amount is set.
  const lateFeeExample = (() => {
    if (!lateFeeEnabled) return null;
    const amt = Number(lateFeeAmountW) || 0;
    if (amt <= 0) return 'Set a late fee amount above 0 for it to apply.';
    const due = Math.max(1, Math.min(28, parseInt(feeDueDayW) || 1));
    const g   = Math.max(0, Number(graceValue) || 0);
    const fee = `NPR ${amt.toLocaleString()}`;
    if (g === 0) {
      return `Example: fee due on day ${due}, no grace → ${fee} late fee added automatically as soon as it's overdue.`;
    }
    if (graceUnit === 'months') {
      return `Example: fee due on day ${due}, grace ${g} month${g === 1 ? '' : 's'} → ${fee} late fee added automatically from day ${due}, ${g === 1 ? 'a month' : `${g} months`} later.`;
    }
    // Days: due day + grace. Due day maxes at 28, so a sum ≤ 28 is always the same month;
    // beyond that it rolls into the next month, so phrase it relatively instead of "day 35".
    const day = due + g;
    const when = day <= 28 ? `from day ${day}` : `${g} days after the due date (early the next month)`;
    return `Example: fee due on day ${due}, grace ${g} day${g === 1 ? '' : 's'} → ${fee} late fee added automatically ${when}.`;
  })();

  // State managed outside react-hook-form
  const [opHours,     setOpHours]     = useState(DEFAULT_HOURS);
  const [gradingRules, setGradingRules] = useState([]);
  const [attendanceNotif, setAttendanceNotif] = useState({
    enabled: false, language: 'ne', notifyOnAbsent: false,
    templates: { attendedNe: '', attendedEn: '', absentNe: '', absentEn: '' },
  });
  const [payMethods,  setPayMethods]  = useState(['Cash', 'Bank Transfer']);
  const [notifs,      setNotifs]      = useState(DEFAULT_NOTIFS);
  const [customDirty, setCustomDirty] = useState(false);
  // Deep-link support (e.g. the Grading page's quiet "Grading rules" link, ?section=attendance-rules)
  // — every other entry into Settings omits the param and gets the unchanged default.
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState(searchParams.get('section') || 'club-info');

  // Location search state
  const [searchQuery,   setSearchQuery]   = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown,  setShowDropdown]  = useState(false);
  const [resolvedAddress, setResolvedAddress] = useState('');
  const [mapCoords,     setMapCoords]     = useState({ lat: 27.7172, lng: 85.3240 });
  const [geoLoading,    setGeoLoading]    = useState(false);
  const searchBoxRef = useRef(null);

  const hasUnsaved = isDirty || customDirty;

  // Tabbed sections: the left/mobile nav switches activeSection; one section shows
  // at a time (no scroll-spy needed).

  // ── Load settings ───────────────────────────────────────────────────────
  const { data, isPending } = useQuery({
    queryKey: ['settings'],
    queryFn:  () => getSettings().then(r => r.data.settings),
  });

  useEffect(() => {
    if (!data) return;
    reset({
      clubName:                 data.clubName              || '',
      clubAddress:              data.clubAddress           || '',
      timezone:                 data.timezone              || 'Asia/Kathmandu',
      clubPhone:                data.clubPhone             || '',
      clubEmail:                data.clubEmail             || '',
      // Canonical Club.clubType (from the tenant club), not the orphan Settings.clubType.
      clubType:                 club?.clubType             || 'taekwondo',
      yearEstablished:          data.yearEstablished       || '',
      'gps.lat':                data.gps?.lat              ?? 27.7172,
      'gps.lng':                data.gps?.lng              ?? 85.3240,
      checkInRadius:            data.checkInRadius         ?? 150,
      monthlyFee:               data.monthlyFee            ?? 0,
      currency:                 data.currency              || 'NPR',
      khaltiPublicKey:          data.khaltiPublicKey       || '',
      esewaProductCode:         data.esewaProductCode      || '',
      feeDueDay:                data.feeDueDay             ?? 1,
      billingCalendar:          data.billingCalendar       || 'ad',
      gracePeriodDays:          data.gracePeriodDays       ?? 7,
      lateFeeGraceUnit:         data.lateFeeGraceUnit      || 'days',
      lateFeeAmount:            data.lateFeeAmount         ?? 0,
      // null = never configured → default the toggle from whether an amount exists.
      lateFeeEnabled:           data.lateFeeEnabled != null ? data.lateFeeEnabled : ((data.lateFeeAmount ?? 0) > 0),
      lateThresholdMinutes:     data.lateThresholdMinutes  ?? 15,
      sessionLockHours:         data.sessionLockHours      ?? 24,
      minAttendanceForExam:     data.minAttendanceForExam  ?? 70,
      sessionTimeoutHours:      data.sessionTimeoutHours   ?? 24,
      registrationMode:         data.registrationMode      || 'demo',
      requireEmailVerification: !!data.requireEmailVerification,
      requireAdminApproval:     !!data.requireAdminApproval,
    });
    if (data.operatingHours?.length)    setOpHours(data.operatingHours);
    if (data.gradingRules?.length)      setGradingRules(data.gradingRules);
    if (data.attendanceNotifications)   setAttendanceNotif({
      enabled: false, language: 'ne', notifyOnAbsent: false,
      ...data.attendanceNotifications,
      templates: { attendedNe: '', attendedEn: '', absentNe: '', absentEn: '', ...data.attendanceNotifications.templates },
    });
    if (data.acceptedPaymentMethods?.length) setPayMethods(data.acceptedPaymentMethods);
    if (data.notifications)             setNotifs({ ...DEFAULT_NOTIFS, ...data.notifications });
    setCustomDirty(false);
  }, [data, reset]);

  const markCustomDirty = useCallback(() => setCustomDirty(true), []);

  // ── Mutations ───────────────────────────────────────────────────────────
  const mutation = useMutation({
    mutationFn: (fd) => updateSettings({
      ...fd,
      gps:                      { lat: parseFloat(fd['gps.lat']), lng: parseFloat(fd['gps.lng']) },
      checkInRadius:            parseInt(fd.checkInRadius)        || 150,
      monthlyFee:               parseFloat(fd.monthlyFee)         || 0,
      yearEstablished:          fd.yearEstablished ? parseInt(fd.yearEstablished) : null,
      feeDueDay:                parseInt(fd.feeDueDay)            || 1,
      gracePeriodDays:          Math.max(0, parseInt(fd.gracePeriodDays) || 0),
      lateFeeGraceUnit:         fd.lateFeeGraceUnit === 'months' ? 'months' : 'days',
      lateFeeAmount:            parseFloat(fd.lateFeeAmount)      || 0,
      // Only commit the on/off toggle (which flips the cron from legacy → policy) when the
      // admin actually edited the Late Fee Policy section — so saving unrelated settings never
      // silently changes an existing club's late-fee behavior.
      ...(['lateFeeEnabled', 'gracePeriodDays', 'lateFeeGraceUnit', 'lateFeeAmount'].some(f => dirtyFields?.[f])
        ? { lateFeeEnabled: !!fd.lateFeeEnabled }
        : {}),
      lateThresholdMinutes:     parseInt(fd.lateThresholdMinutes) || 15,
      sessionLockHours:         parseInt(fd.sessionLockHours)     || 24,
      minAttendanceForExam:     parseInt(fd.minAttendanceForExam) || 70,
      sessionTimeoutHours:      parseInt(fd.sessionTimeoutHours)  || 24,
      requireEmailVerification: !!fd.requireEmailVerification,
      requireAdminApproval:     !!fd.requireAdminApproval,
      operatingHours: opHours,
      acceptedPaymentMethods: payMethods,
      notifications: notifs,
      gradingRules,
      attendanceNotifications: attendanceNotif,
    }),
    onSuccess: () => {
      toast.success('Settings saved');
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      reset(getValues());
      setCustomDirty(false);
    },
    onError: () => toast.error('Failed to save settings'),
  });

  // Theme mutation removed (Step 3) — the navbar's sun/moon toggle is now the only place
  // that calls updateTheme(); this page no longer has a theme control to drive it.

  // Calendar mutations retired (Step 2) — display is unconditionally BS; the club-wide and
  // per-user calendar cards were removed. isPrimaryAdmin stays; it also gates the Tax card
  // (moved from Appearance to Fees & Billing in Step 3, still gated the same way).
  const isPrimaryAdmin = user?.adminType === 'primary' || user?.isPrimaryAdmin === true;

  // ── Tax info / PAN (Primary Admin only) ──────────────────────────────────
  const [pan, setPan] = useState(club?.pan || '');
  useEffect(() => { setPan(club?.pan || ''); }, [club?.pan]);

  const panMutation = useMutation({
    mutationFn: (value) => updateClubSettings({ pan: value }),
    onSuccess:  (_, value) => {
      if (club) setClubDirectly({ ...club, pan: value }, slug);
      toast.success('Tax information updated');
    },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to update tax information'),
  });

  const onSave = handleSubmit(d => mutation.mutate(d));

  // ── Operating hours helpers ─────────────────────────────────────────────
  const updateHour = useCallback((idx, field, val) => {
    setOpHours(prev => prev.map((h, i) => i === idx ? { ...h, [field]: val } : h));
    markCustomDirty();
  }, [markCustomDirty]);

  const updateAttendanceNotif = useCallback((field, val) => {
    setAttendanceNotif(prev => ({ ...prev, [field]: val }));
    markCustomDirty();
  }, [markCustomDirty]);
  const updateAttendanceNotifTemplate = useCallback((field, val) => {
    setAttendanceNotif(prev => ({ ...prev, templates: { ...prev.templates, [field]: val } }));
    markCustomDirty();
  }, [markCustomDirty]);

  const updateGradingRule = useCallback((belt, field, val) => {
    setGradingRules(prev => prev.map(r => r.belt === belt ? { ...r, [field]: val } : r));
    markCustomDirty();
  }, [markCustomDirty]);

  const togglePayMethod = useCallback((method) => {
    setPayMethods(prev =>
      prev.includes(method) ? prev.filter(m => m !== method) : [...prev, method]
    );
    markCustomDirty();
  }, [markCustomDirty]);

  const toggleNotif = useCallback((key) => {
    setNotifs(prev => ({ ...prev, [key]: !prev[key] }));
    markCustomDirty();
  }, [markCustomDirty]);

  // ── Click-outside: close search dropdown ───────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Reverse geocode saved coordinates on data load ──────────────────────
  // (kept separate from the main data effect to avoid reset dependency issues)
  useEffect(() => {
    if (!data?.gps?.lat || !data?.gps?.lng) return;
    const lat = data.gps.lat;
    const lng = data.gps.lng;
    setMapCoords({ lat, lng });
    fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
      { headers: { 'Accept-Language': 'en', 'User-Agent': 'TrainAxis/1.0' } }
    )
      .then(r => r.json())
      .then(json => { if (json.display_name) setResolvedAddress(json.display_name); })
      .catch(() => {});
  }, [data]);

  // ── Nominatim address search (debounced 500ms) ──────────────────────────
  useEffect(() => {
    if (searchQuery.length < 3) { setSearchResults([]); setShowDropdown(false); return; }
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res  = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&format=json&limit=5&addressdetails=1`,
          { headers: { 'Accept-Language': 'en', 'User-Agent': 'TrainAxis/1.0' } }
        );
        const json = await res.json();
        setSearchResults(json);
        setShowDropdown(true);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ── Select a search result ──────────────────────────────────────────────
  const selectResult = useCallback((result) => {
    const lat = parseFloat(parseFloat(result.lat).toFixed(6));
    const lng = parseFloat(parseFloat(result.lon).toFixed(6));
    setValue('gps.lat', lat, { shouldDirty: true });
    setValue('gps.lng', lng, { shouldDirty: true });
    setMapCoords({ lat, lng });
    setResolvedAddress(result.display_name);
    setSearchQuery('');
    setSearchResults([]);
    setShowDropdown(false);
  }, [setValue]);

  // ── Use browser geolocation ─────────────────────────────────────────────
  const useMyLocation = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = parseFloat(pos.coords.latitude.toFixed(6));
        const lng = parseFloat(pos.coords.longitude.toFixed(6));
        setValue('gps.lat', lat, { shouldDirty: true });
        setValue('gps.lng', lng, { shouldDirty: true });
        setMapCoords({ lat, lng });
        try {
          const res  = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
            { headers: { 'Accept-Language': 'en', 'User-Agent': 'TrainAxis/1.0' } }
          );
          const json = await res.json();
          setResolvedAddress(json.display_name || `${lat}, ${lng}`);
        } catch {
          setResolvedAddress(`${lat}, ${lng}`);
        }
        setGeoLoading(false);
      },
      () => {
        setGeoLoading(false);
        toast.error('Location access denied. Please enter coordinates manually.');
      }
    );
  }, [setValue]);

  // ── Commit map coords when focus leaves the lat/lng input group ─────────
  const handleCoordsBlur = useCallback((e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      const lat = parseFloat(getValues('gps.lat'));
      const lng = parseFloat(getValues('gps.lng'));
      if (!isNaN(lat) && !isNaN(lng)) setMapCoords({ lat, lng });
    }
  }, [getValues]);

  if (isPending) return (
    <div className="space-y-4">
      {[1, 2, 3].map(i => <div key={i} className="h-32 bg-nav-hover animate-pulse rounded-2xl" />)}
    </div>
  );

  // Membership Plans is a gym/yoga/general feature — taekwondo clubs never see it (and the
  // backend rejects it too). Insert it right after Fees & Billing for non-taekwondo clubs.
  const isTaekwondo = (club?.clubType || 'taekwondo') === 'taekwondo';
  const navItems = isTaekwondo
    ? NAV_ITEMS
    : NAV_ITEMS.flatMap(item =>
        item.id === 'fees'
          ? [item, { id: 'membership-plans', label: 'Membership Plans', Icon: MdCardMembership }]
          : [item]
      );

  // ── Sidebar nav ─────────────────────────────────────────────────────────
  const SidebarNav = () => (
    <nav className="space-y-0.5">
      {navItems.map(({ id, label, Icon }) => (
        <button
          key={id}
          type="button"
          onClick={() => setActiveSection(id)}
          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-all text-left ${
            activeSection === id
              ? 'bg-nav-hover text-primary'
              : 'text-muted hover:bg-nav-hover hover:text-secondary'
          }`}
        >
          <Icon size={16} className="flex-shrink-0" />
          <span className="truncate">{label}</span>
        </button>
      ))}
    </nav>
  );

  return (
    <div>
      {/* Mobile horizontal tabs */}
      <div className="flex lg:hidden gap-1.5 overflow-x-auto pb-3 mb-5 -mx-1 px-1">
        {navItems.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setActiveSection(id)}
            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border transition-all ${
              activeSection === id
                ? 'bg-primary text-white border-primary'
                : 'bg-card text-muted border-divider'
            }`}
          >
            <Icon size={13} />
            {label}
          </button>
        ))}
      </div>

      <div className="flex gap-7">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block w-48 flex-shrink-0">
          <div className="sticky top-4">
            <SidebarNav />
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-10">

          {/* ── 1. CLUB INFO ──────────────────────────────────────────── */}
          {activeSection === 'club-info' && (
          <div>
            <SectionHead id="club-info" Icon={MdSettings} title="Club Info" />
            <Card>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Club Name"    {...register('clubName')} />
                <Input label="Club Address" {...register('clubAddress')} />
                <Input label="Club Phone" error={errors.clubPhone?.message}
                  {...register('clubPhone', { validate: (v) => isValidPhone(v) || PHONE_HINT })}
                  onChange={(e) => setValue('clubPhone', sanitizePhone(e.target.value), { shouldValidate: true, shouldDirty: true })}
                  placeholder="+977-01-0000000" />
                <Input label="Club Email (public)" error={errors.clubEmail?.message}
                  {...register('clubEmail', { validate: (v) => isValidEmail(v) || EMAIL_HINT })}
                  onChange={(e) => setValue('clubEmail', sanitizeEmail(e.target.value), { shouldValidate: true, shouldDirty: true })}
                  placeholder="info@yourclub.com" />
                <div>
                  <FieldLabel>Club Type</FieldLabel>
                  <select {...register('clubType')} className={SELECT_CLS}>
                    {CLUB_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <Input label="Year Established" type="number" min="1900" max={new Date().getFullYear()}
                  placeholder="e.g. 2015" {...register('yearEstablished')} />
                <div>
                  <FieldLabel>Timezone</FieldLabel>
                  <select {...register('timezone')} className={SELECT_CLS}>
                    {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                  </select>
                </div>
                <div>
                  <FieldLabel>Default Currency</FieldLabel>
                  <select {...register('currency')} className={SELECT_CLS}>
                    <option value="NPR">NPR (Nepalese Rupee)</option>
                    <option value="USD">USD</option>
                    <option value="AUD">AUD</option>
                    <option value="GBP">GBP</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>

              {/* Member access — invite-only (folded from Registration & Access) */}
              <div className="mt-5 pt-5 border-t border-divider flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-nav-hover flex items-center justify-center flex-shrink-0">
                  <MdShield className="text-primary text-lg" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-secondary">Member access</p>
                    <span className="text-[11px] font-bold text-emerald-400 bg-emerald-500/12 px-2 py-0.5 rounded-full">Invite-only</span>
                  </div>
                  <p className="text-xs text-muted mt-1 leading-snug">
                    New members join by invitation only. Add {v.members.toLowerCase()} or trainers from their pages, or send an email invite — there is no public sign-up page.
                  </p>
                </div>
              </div>
            </Card>
          </div>
          )}

          {/* ── 2. LOCATION & CHECK-IN ────────────────────────────────── */}
          {activeSection === 'location' && (
          <div>
            <SectionHead id="location" Icon={MdLocationOn} title="Location & Check-in" />
            <Card>
              <p className="text-xs text-subtle mb-4">
                Used to verify trainer and {v.member.toLowerCase()} GPS check-ins. Search for your club address or drop a pin to set exact coordinates.
              </p>

              {/* Address search + Use My Location */}
              <div className="mb-4" ref={searchBoxRef}>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-secondary">
                    Search by address or place name
                  </label>
                  <button
                    type="button"
                    onClick={useMyLocation}
                    disabled={geoLoading}
                    className="flex items-center gap-1.5 text-xs text-primary font-medium hover:underline disabled:opacity-60 flex-shrink-0 ml-3"
                  >
                    <MdLocationOn size={14} />
                    {geoLoading ? 'Locating…' : 'Use My Location'}
                  </button>
                </div>

                <div className="relative">
                  <div className="flex items-center border border-divider rounded-xl bg-card focus-within:ring-2 focus-within:ring-primary/30 overflow-hidden">
                    <MdLocationOn className="text-subtle flex-shrink-0 ml-3" size={16} />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                      placeholder="e.g. Swayambhu, Kathmandu or Thamel Chowk, Nepal"
                      className="flex-1 py-2.5 px-3 text-sm bg-transparent text-secondary placeholder:text-subtle focus:outline-none"
                    />
                    {searchLoading && (
                      <svg className="animate-spin mr-3 h-4 w-4 text-primary flex-shrink-0" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                    )}
                    {searchQuery && !searchLoading && (
                      <button
                        type="button"
                        onClick={() => { setSearchQuery(''); setSearchResults([]); setShowDropdown(false); }}
                        className="mr-2 text-subtle hover:text-secondary flex-shrink-0"
                      >
                        ×
                      </button>
                    )}
                  </div>

                  {/* Results dropdown */}
                  {showDropdown && (
                    <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-card border border-divider rounded-xl shadow-lg overflow-hidden">
                      {searchResults.length > 0 ? (
                        searchResults.map((r, i) => (
                          <button
                            key={i}
                            type="button"
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => selectResult(r)}
                            className="w-full text-left px-4 py-3 hover:bg-nav-hover border-b border-divider last:border-0 transition-colors"
                          >
                            <p className="text-sm font-semibold text-secondary truncate">
                              {r.name || r.display_name.split(',')[0]}
                            </p>
                            <p className="text-xs text-subtle mt-0.5 truncate">{r.display_name}</p>
                          </button>
                        ))
                      ) : (
                        !searchLoading && searchQuery.length >= 3 && (
                          <div className="px-4 py-3 text-sm text-subtle">No results found</div>
                        )
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Lat / Lng / Radius — blur group to update map */}
              <div
                className="grid grid-cols-1 sm:grid-cols-3 gap-4"
                onBlur={handleCoordsBlur}
              >
                <Input label="Latitude"  type="number" step="any" {...register('gps.lat')} />
                <Input label="Longitude" type="number" step="any" {...register('gps.lng')} />
                <div>
                  <Input label="Check-in Radius (metres)" type="number" min="10" max="1000" {...register('checkInRadius')} />
                  <p className="text-xs text-subtle mt-1">Default: 150m</p>
                </div>
              </div>

              {/* Resolved address display */}
              {resolvedAddress && (
                <div className="mt-3 flex items-start gap-2 px-3 py-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                  <MdLocationOn className="text-emerald-400 flex-shrink-0 mt-0.5" size={14} />
                  <p className="text-xs text-emerald-400 leading-relaxed">
                    <span className="font-semibold">Location set: </span>{resolvedAddress}
                  </p>
                </div>
              )}

              {/* Map preview */}
              <div className="mt-4">
                <p className="text-xs text-subtle mb-2">Map preview</p>
                <iframe
                  title="Club location"
                  key={`${mapCoords.lat},${mapCoords.lng}`}
                  src={`https://maps.google.com/maps?q=${mapCoords.lat},${mapCoords.lng}&z=16&output=embed`}
                  className="w-full h-52 rounded-xl border border-divider"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <p className="text-[10px] text-subtle mt-1">
                  © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer" className="hover:underline">OpenStreetMap contributors</a>
                </p>
              </div>

              {/* Operating Hours */}
              <div className="mt-6 pt-5 border-t border-divider">
                <p className="text-xs font-semibold text-subtle uppercase tracking-wide mb-3">
                  Operating Hours
                </p>
                <p className="text-xs text-subtle mb-4">Shown on the public club profile page.</p>
                <div className="space-y-1">
                  {opHours.map((oh, i) => (
                    <div key={oh.day} className="flex items-center gap-3 py-2 border-b border-divider last:border-0">
                      <Toggle
                        checked={oh.isOpen}
                        onChange={v => updateHour(i, 'isOpen', v)}
                      />
                      <span className="w-24 text-sm text-secondary font-medium flex-shrink-0">{oh.day}</span>
                      {oh.isOpen ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="time"
                            value={oh.openTime}
                            onChange={e => updateHour(i, 'openTime', e.target.value)}
                            className="border border-divider rounded-lg px-2 py-1.5 text-xs text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 bg-card"
                          />
                          <span className="text-xs text-subtle">to</span>
                          <input
                            type="time"
                            value={oh.closeTime}
                            onChange={e => updateHour(i, 'closeTime', e.target.value)}
                            className="border border-divider rounded-lg px-2 py-1.5 text-xs text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 bg-card"
                          />
                        </div>
                      ) : (
                        <span className="text-xs text-subtle">Closed</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </div>
          )}

          {/* ── 3. FEES & BILLING ─────────────────────────────────────── */}
          {activeSection === 'fees' && (
          <div>
            <SectionHead id="fees" Icon={MdPayments} title="Fees & Billing" />

            <div className="space-y-6">
              {/* 1 · Monthly Fee */}
              <Card>
                <h3 className="text-sm font-semibold text-secondary mb-4">Monthly Fee</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel sub={`Charged to each ${v.member.toLowerCase()} every month`}>Default Monthly Fee (NPR)</FieldLabel>
                    <Input type="number" min="0" step="0.01" className="[&_input]:min-h-[44px]" {...register('monthlyFee')} />
                  </div>
                  <div>
                    <FieldLabel sub="Day of the month fees are due (1–28)">Fee Due Day</FieldLabel>
                    <Input type="number" min="1" max="28" className="[&_input]:min-h-[44px]" {...register('feeDueDay')} />
                  </div>
                </div>
              </Card>

              {/* 1b · Billing Calendar (Path B, Stage 3) — gym vertical excluded (CLAUDE.md:
                  paused and untested, must stay dormant), same gate the backend enforces. */}
              {club?.clubType !== 'gym' && (
                <Card>
                  <h3 className="text-sm font-semibold text-secondary mb-1">Billing Calendar</h3>
                  <p className="text-xs text-subtle mb-4">
                    Which calendar a {v.member.toLowerCase()}'s first month is prorated against when they join mid-month.
                    Only affects a brand-new {v.member.toLowerCase()}'s very first charge — recurring full months, due
                    dates, and reports are unaffected either way.
                  </p>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setValue('billingCalendar', 'ad', { shouldDirty: true })}
                      disabled={isReadOnly}
                      title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                      className={`flex-1 px-4 py-3 rounded-xl border-2 font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        billingCalendarW === 'ad'
                          ? 'bg-primary border-primary text-white'
                          : 'border-divider text-secondary hover:border-divider bg-card'
                      }`}
                    >
                      AD (Gregorian) — default
                    </button>
                    <button
                      type="button"
                      onClick={() => setValue('billingCalendar', 'bs', { shouldDirty: true })}
                      disabled={isReadOnly}
                      title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                      className={`flex-1 px-4 py-3 rounded-xl border-2 font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        billingCalendarW === 'bs'
                          ? 'bg-primary border-primary text-white'
                          : 'border-divider text-secondary hover:border-divider bg-card'
                      }`}
                    >
                      BS (Bikram Sambat)
                    </button>
                  </div>
                  <p className="text-[11px] text-subtle mt-3">
                    Example: joining Ashwin 23 of a 31-day Ashwin charges for the 9 remaining BS
                    days under BS, or the 23 remaining AD days of whichever Gregorian month that
                    falls in under AD.
                  </p>
                </Card>
              )}

              {/* 2 · Late Fees */}
              <Card>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-semibold text-secondary">Late Fees</h3>
                    <p className="text-xs text-subtle mt-0.5">Automatically add a fee to overdue monthly payments.</p>
                  </div>
                  <Toggle checked={!!lateFeeEnabled} onChange={v => setValue('lateFeeEnabled', v, { shouldDirty: true })} />
                </div>

                {lateFeeEnabled && (
                  <div className="space-y-4 mt-4 pt-4 border-t border-divider">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <FieldLabel sub="Extra days after the due date before a late fee is added">Grace period</FieldLabel>
                        <div className="flex gap-2">
                          <Input type="number" min="0" max="60" className="flex-1 [&_input]:min-h-[44px]" {...register('gracePeriodDays')} />
                          <select
                            {...register('lateFeeGraceUnit')}
                            className="min-h-[44px] rounded-xl border border-divider bg-card text-secondary text-sm px-3 focus:outline-none focus:border-primary"
                          >
                            <option value="days">days</option>
                            <option value="months">months</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <FieldLabel sub="Added to the overdue fee">Late fee amount (NPR)</FieldLabel>
                        <Input type="number" min="0" step="1" className="[&_input]:min-h-[44px]" {...register('lateFeeAmount')} />
                      </div>
                    </div>
                    {lateFeeExample && (
                      <div className="rounded-xl bg-nav-hover px-4 py-3">
                        <p className="text-xs text-secondary">{lateFeeExample}</p>
                      </div>
                    )}
                  </div>
                )}
              </Card>

              {/* 3 · Payment Methods */}
              <Card>
                <h3 className="text-sm font-semibold text-secondary mb-1">Payment Methods</h3>
                <p className="text-xs text-subtle">Only ticked methods appear when recording a payment and when {v.members.toLowerCase()} pay.</p>
                <p className="text-xs text-muted mb-4">{v.members} only see methods that are ticked AND have details/QR uploaded below.</p>
                <div className="flex flex-wrap gap-2.5">
                  {PAYMENT_METHODS.map(method => (
                    <label
                      key={method}
                      className={`flex items-center gap-2 cursor-pointer min-h-[44px] px-3.5 rounded-xl border transition-all duration-200 ${
                        payMethods.includes(method)
                          ? 'border-secondary bg-nav-hover'
                          : 'border-divider hover:border-secondary hover:bg-nav-hover'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={payMethods.includes(method)}
                        onChange={() => togglePayMethod(method)}
                        className="w-4 h-4 accent-primary rounded"
                      />
                      <span className="text-sm text-secondary">{method}</span>
                    </label>
                  ))}
                </div>
              </Card>

              {/* 4 · Payment Details Students See — merged bank details + QR tiles (writes to the
                  paymentSettings store students see; the old Settings-model bank fields are dropped). */}
              <Card>
                <PaymentMethodsSection />
              </Card>

              {/* 5 · Opening Balances (Ledger) — dated seed, Primary-Admin only, own endpoint */}
              <OpeningBalancesCard />

              {/* 6 · Tax Information — Primary Admin only. PAN appears on exported financial
                  reports, so it lives with the other money settings, not under Appearance.
                  Own isolated mutation/button (panMutation), decoupled from the section's
                  react-hook-form fields above — saving this never touches, and is never
                  touched by, the sticky "Save Settings" button at the bottom of the page. */}
              {isPrimaryAdmin && (
                <Card>
                  <p className="text-xs font-semibold text-subtle uppercase tracking-wide mb-1">Tax Information</p>
                  <h3 className="text-sm font-semibold text-secondary mb-1">PAN Number</h3>
                  <p className="text-xs text-subtle mb-4">
                    Shown on exported financial reports. Leave blank if not yet registered.
                  </p>
                  <input
                    value={pan}
                    onChange={e => setPan(e.target.value)}
                    placeholder="e.g. 123456789"
                    className="w-full max-w-xs border border-divider rounded-xl px-3 py-2.5 text-sm bg-card text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  <div className="mt-4 flex items-center gap-3">
                    <Button
                      onClick={() => panMutation.mutate(pan.trim())}
                      disabled={pan.trim() === (club?.pan || '') || panMutation.isPending || isReadOnly}
                      title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                    >
                      <MdSave /> {panMutation.isPending ? 'Saving…' : 'Save Changes'}
                    </Button>
                    {pan.trim() !== (club?.pan || '') && (
                      <span className="text-xs text-subtle">Unsaved change</span>
                    )}
                  </div>
                </Card>
              )}
            </div>
          </div>
          )}

          {/* ── MEMBERSHIP PLANS (gym / yoga / general only) ──────────── */}
          {activeSection === 'membership-plans' && !isTaekwondo && (
            <MembershipPlansSection />
          )}

          {/* ── 4. ATTENDANCE RULES ───────────────────────────────────── */}
          {activeSection === 'attendance-rules' && (
          <div>
            <SectionHead id="attendance-rules" Icon={MdAccessTime} title="Attendance Rules" />
            <Card>
              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel sub={`${v.members} arriving more than ___ minutes late are marked Late`}>
                      Late Threshold (minutes)
                    </FieldLabel>
                    <Input type="number" min="0" max="120" {...register('lateThresholdMinutes')} />
                  </div>
                  <div>
                    <FieldLabel sub="Attendance can be edited up to ___ hours after session ends">
                      Session Lock (hours)
                    </FieldLabel>
                    <Input type="number" min="0" max="168" {...register('sessionLockHours')} />
                  </div>
                </div>

                <div>
                  <FieldLabel sub={`${v.members} need ___% attendance to be eligible for belt exam`}>
                    Minimum Attendance for Belt Exam (%)
                  </FieldLabel>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      className="max-w-xs"
                      {...register('minAttendanceForExam')}
                    />
                  </div>
                  <p className="text-xs text-subtle mt-1.5">
                    Currently informational only — eligibility enforcement coming soon.
                  </p>
                </div>
              </div>
            </Card>

            {hasBelts(club?.clubType) && (
              <Card className="mt-6">
                <h3 className="text-sm font-semibold text-secondary mb-1">Grading Rules</h3>
                <p className="text-xs text-subtle mb-4">
                  Minimum time and sessions a {v.member.toLowerCase()} needs at their current belt before the
                  next one. The Grading view uses these to show who's ELIGIBLE — the instructor still decides
                  who actually grades.
                </p>
                <div className="space-y-1">
                  {gradingRules.map((r) => {
                    const nextIdx = BELT_ORDER.indexOf(r.belt) + 1;
                    const nextBelt = BELT_ORDER[nextIdx];
                    return (
                      <div key={r.belt} className="flex items-center gap-3 py-2 border-b border-divider last:border-0">
                        <span className="w-44 text-sm text-secondary font-medium flex-shrink-0 truncate">
                          {r.belt} → {nextBelt}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <input type="number" min="0" value={r.minMonths}
                            onChange={e => updateGradingRule(r.belt, 'minMonths', parseInt(e.target.value) || 0)}
                            className="w-16 border border-divider rounded-lg px-2 py-1.5 text-xs text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 bg-card" />
                          <span className="text-xs text-subtle">months,</span>
                          <input type="number" min="0" value={r.minSessions}
                            onChange={e => updateGradingRule(r.belt, 'minSessions', parseInt(e.target.value) || 0)}
                            className="w-16 border border-divider rounded-lg px-2 py-1.5 text-xs text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 bg-card" />
                          <span className="text-xs text-subtle">sessions</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {club?.clubType !== 'gym' && (
              <Card className="mt-6">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-sm font-semibold text-secondary">Guardian Attendance Messages</h3>
                  <div className="flex items-center gap-3">
                    <Link to="/admin/attendance-messages" className="text-xs text-primary font-semibold hover:underline">
                      View sent messages
                    </Link>
                    <Toggle checked={attendanceNotif.enabled} onChange={v => updateAttendanceNotif('enabled', v)} />
                  </div>
                </div>
                <p className="text-xs text-subtle mb-4">
                  Sends an SMS to a student's guardian the moment they're marked present — never for
                  backdated/catch-up attendance, and at most once per student per day. Off until you
                  turn it on AND write the message text below — nothing is sent with a blank template.
                </p>

                {attendanceNotif.enabled && (
                  <div className="space-y-5">
                    <div>
                      <FieldLabel sub="Which language(s) the message is sent in">Language</FieldLabel>
                      <select className={SELECT_CLS} value={attendanceNotif.language}
                        onChange={e => updateAttendanceNotif('language', e.target.value)}>
                        <option value="ne">Nepali only</option>
                        <option value="en">English only</option>
                        <option value="both">Both (Nepali, then English)</option>
                      </select>
                    </div>

                    <div className="flex items-center gap-3">
                      <Toggle checked={attendanceNotif.notifyOnAbsent} onChange={v => updateAttendanceNotif('notifyOnAbsent', v)} />
                      <div>
                        <p className="text-sm text-secondary">Also message when absent</p>
                        <p className="text-xs text-subtle">Off by default — most clubs only want the positive "attended" message.</p>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-divider">
                      <p className="text-xs font-semibold text-muted mb-2">Message text</p>
                      <p className="text-xs text-subtle mb-3">
                        Placeholders: <code className="text-[11px] bg-nav-hover px-1 rounded">{'{student}'}</code>{' '}
                        <code className="text-[11px] bg-nav-hover px-1 rounded">{'{class}'}</code>{' '}
                        <code className="text-[11px] bg-nav-hover px-1 rounded">{'{time}'}</code>{' '}
                        <code className="text-[11px] bg-nav-hover px-1 rounded">{'{club}'}</code>
                      </p>
                      <div className="space-y-3">
                        {(attendanceNotif.language === 'ne' || attendanceNotif.language === 'both') && (
                          <div>
                            <FieldLabel>Attended — Nepali</FieldLabel>
                            <textarea className={SELECT_CLS} rows={2} value={attendanceNotif.templates.attendedNe}
                              onChange={e => updateAttendanceNotifTemplate('attendedNe', e.target.value)} />
                          </div>
                        )}
                        {(attendanceNotif.language === 'en' || attendanceNotif.language === 'both') && (
                          <div>
                            <FieldLabel>Attended — English</FieldLabel>
                            <textarea className={SELECT_CLS} rows={2} value={attendanceNotif.templates.attendedEn}
                              onChange={e => updateAttendanceNotifTemplate('attendedEn', e.target.value)} />
                          </div>
                        )}
                        {attendanceNotif.notifyOnAbsent && (attendanceNotif.language === 'ne' || attendanceNotif.language === 'both') && (
                          <div>
                            <FieldLabel>Absent — Nepali</FieldLabel>
                            <textarea className={SELECT_CLS} rows={2} value={attendanceNotif.templates.absentNe}
                              onChange={e => updateAttendanceNotifTemplate('absentNe', e.target.value)} />
                          </div>
                        )}
                        {attendanceNotif.notifyOnAbsent && (attendanceNotif.language === 'en' || attendanceNotif.language === 'both') && (
                          <div>
                            <FieldLabel>Absent — English</FieldLabel>
                            <textarea className={SELECT_CLS} rows={2} value={attendanceNotif.templates.absentEn}
                              onChange={e => updateAttendanceNotifTemplate('absentEn', e.target.value)} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            )}
          </div>
          )}

          {/* ── 5. NOTIFICATIONS ──────────────────────────────────────── */}
          {activeSection === 'notifications' && (
          <div>
            <SectionHead id="notifications" Icon={MdNotifications} title="Notifications" />
            <Card>
              <p className="text-xs text-muted mb-5">
                Choose which events notify your admins by email or in the in-app bell.
              </p>

              {[
                {
                  group: 'Email',
                  caption: 'Sent to all admin accounts for this club.',
                  items: [
                    { key: 'paymentOverdue',    label: 'Payment overdue alert', sub: `When a ${v.member.toLowerCase()} becomes overdue` },
                    { key: 'beltExamScheduled', label: 'Belt exam scheduled',   sub: 'When an exam date is set' },
                  ],
                },
                {
                  group: 'In-app',
                  caption: 'Shown in the bell icon.',
                  items: [
                    { key: 'sessionCreated',     label: 'Session created',        sub: 'When a class session is added' },
                    { key: 'attendanceMarked',   label: 'Attendance marked',      sub: 'When a trainer submits attendance' },
                    { key: 'announcementPosted', label: 'New announcement posted', sub: 'When an announcement goes out' },
                  ],
                },
              ].map(({ group, caption, items }) => (
                <div key={group} className="mb-6 last:mb-0">
                  <div className="flex items-baseline gap-2 mb-2.5">
                    <p className="text-xs font-semibold text-subtle uppercase tracking-wider">{group}</p>
                    <p className="text-[11px] text-subtle">{caption}</p>
                  </div>
                  <div className="rounded-xl border border-divider divide-y divide-divider overflow-hidden">
                    {items.map(({ key, label, sub }) => (
                      <div key={key} className="flex items-center justify-between gap-4 px-4 py-3.5 hover:bg-nav-hover/40 transition-colors">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-secondary">{label}</p>
                          {sub && <p className="text-xs text-muted mt-0.5">{sub}</p>}
                        </div>
                        <Toggle
                          checked={!!notifs[key]}
                          onChange={() => toggleNotif(key)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </Card>
          </div>
          )}

          {/* ── APPEARANCE ────────────────────────────────────────────── */}
          {activeSection === 'appearance' && (
          <div>
            <SectionHead id="appearance" Icon={MdPalette} title="Appearance" />

            {/* Profile photo — the logged-in admin's OWN photo (self-upload) */}
            <Card className="mb-5">
              <p className="text-xs font-semibold text-subtle uppercase tracking-wide mb-1">Profile Photo</p>
              <h3 className="text-sm font-semibold text-secondary mb-3">Your photo</h3>
              <div className="flex items-center gap-4">
                <div
                  title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                  className={isReadOnly ? 'opacity-50 cursor-not-allowed' : undefined}
                >
                  <div className={isReadOnly ? 'pointer-events-none' : undefined}>
                    <AvatarUploader
                      currentPhotoUrl={user?.profilePhoto || null}
                      studentName={user?.name}
                      uploadUrl="/api/admin/users/me/photo"
                      size={96}
                      onUploaded={(url) => {
                        updateUser({ profilePhoto: url }); // patches useAuth user → navbar updates live
                        toast.success('Profile photo updated');
                      }}
                    />
                  </div>
                </div>
                <p className="text-xs text-subtle max-w-xs leading-relaxed">
                  Shown in the top bar and on the Admins page. Click the photo to upload and crop a square image.
                </p>
              </div>
            </Card>

            {/* Theme buttons removed (Step 3) — the sun/moon toggle in the top navbar now owns
                this, writing through the same applyTheme/updateTheme path. Kept as a pointer,
                not a Card, since there's nothing left here to act on. */}
            <p className="text-xs text-subtle">
              Dashboard theme is switched from the sun/moon icon in the top bar.
            </p>

            {/* Calendar Preference retired (Step 2): display is now unconditionally Bikram
                Sambat with a muted AD secondary. The per-user and club-wide calendar cards
                were removed; Club.defaultCalendar / User.calendarPreference remain as frozen
                dead fields (still read by Finance grouping + crons until that moves to BS). */}

            {/* Tax Information moved to Fees & Billing (Step 3) — it's a money-export field,
                not an appearance one. See the "fees" section below. */}
          </div>
          )}

          {/* ── 8. SECURITY & ACCESS LOG ──────────────────────────────── */}
          {activeSection === 'security' && (
          <div>
            <SectionHead id="security" Icon={MdShield} title="Security & Access Log" />
            <TwoFactorCard />
            <Card>
              <div className="mb-5 pb-5 border-b border-divider">
                <FieldLabel sub="Admins and trainers are automatically logged out after this duration">
                  Session Timeout
                </FieldLabel>
                <select {...register('sessionTimeoutHours')} className={`${SELECT_CLS} max-w-xs`}>
                  <option value={1}>1 hour</option>
                  <option value={4}>4 hours</option>
                  <option value={8}>8 hours</option>
                  <option value={24}>24 hours (recommended)</option>
                  <option value={168}>7 days</option>
                </select>
              </div>

              <p className="text-xs font-semibold text-subtle uppercase tracking-wide mb-3">Recent Platform Access</p>
              <PlatformAccessCard />
            </Card>
          </div>
          )}

          {/* Bottom spacer so sticky button doesn't cover last section */}
          <div className="h-20" />
        </div>
      </div>

      {/* ── Sticky save button ───────────────────────────────────────────── */}
      <div
        className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 transition-all duration-200 ${
          hasUnsaved ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'
        }`}
      >
        <span className="bg-amber-500/12 text-amber-400 text-xs font-medium px-3 py-1.5 rounded-full border border-amber-500/20 shadow-sm hidden sm:inline-flex items-center gap-1">
          <span className="w-1.5 h-1.5 bg-amber-400 rounded-full" />
          Unsaved changes
        </span>
        <button
          type="button"
          onClick={isReadOnly ? undefined : onSave}
          disabled={mutation.isPending || isReadOnly}
          title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
          className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white text-sm font-semibold rounded-2xl shadow-lg hover:opacity-90 transition-opacity disabled:opacity-60"
        >
          <MdSave size={17} />
          {mutation.isPending ? 'Saving…' : isReadOnly ? 'Read-only mode' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}

export default Settings;
